import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Announcer } from '../components/Announcer.tsx';

/**
 * Dire una cosa sola volta, e dirla a tutti.
 *
 * Le schermate amministrative facevano la stessa cosa in nove punti: due stati,
 * una funzione `say`, una regione live montata e vuota, e un banner sotto. Erano
 * nove copie della stessa forma, e la regola del progetto è "due si tollerano,
 * tre si estraggono" — nove erano un invito a dimenticarsene una, che è
 * esattamente com'è andata: due sezioni della fase 7 scrivevano il banner senza
 * montare la regione live, e con l'API caduta un fornitore cieco restava su
 * "Caricamento…" (`docs/DECISIONI.md`, AJ15).
 *
 * Qui la forma è una sola, e non si può montarne metà.
 *
 * Chi la usa deve montare `announcer` **nella stessa posizione in ogni ramo**
 * del proprio disegno: React distrugge e ricrea un nodo che cambia posizione, e
 * una regione live che compare già piena non viene annunciata da quasi nessuna
 * combinazione di browser e screen reader (AM4).
 *
 * Il contatore serve a far **rileggere lo stesso messaggio**: una regione live
 * annuncia quando il contenuto cambia, e chi riprova un'azione che fallisce
 * allo stesso modo non sentirebbe niente la seconda volta.
 *
 * Due schermate restano fuori, e non per debito: `ShiftsPage` e
 * `NotificationsPage` non hanno un banner **di pagina**. La prima sposta il
 * messaggio d'errore accanto al turno che non è riuscito a prenotare, e per
 * farlo deve rileggersi il testo che ha appena detto; la seconda tiene il
 * riquadro dentro la scheda delle notifiche, accanto ai pulsanti che lo
 * producono. In entrambe la regione e il banner **non sono lo stesso nodo**, e
 * consegnare la sola regione è precisamente ciò che questa astrazione esiste
 * per rendere impossibile (AO1).
 */

/**
 * Come si vede un esito, quando si vede.
 *
 * `danger` è per ciò che **non è riuscito**. Non sostituisce il testo: il
 * messaggio dice già cos'è andato storto, e il colore è la seconda copia di
 * quell'informazione — nessuna informazione affidata al solo colore
 * (sezione 7bis).
 */
/**
 * Come si dice una cosa.
 *
 *  - `ok` e `danger`: si annuncia **e** si scrive a schermo, perché è una
 *    notizia che chi guarda non ha ancora.
 *  - `quiet`: si annuncia e basta. Serve quando la stessa cosa è **già
 *    visibile**: da quando gli elenchi dichiarano quanti elementi hanno
 *    accanto al titolo, l'annuncio "2 risultati" compariva anche in un
 *    riquadro sotto, cioè lo stesso numero scritto due volte a due dita di
 *    distanza. Toglierlo del tutto non si poteva: chi non guarda lo schermo
 *    resta con il fuoco sul filtro appena usato e non ha nessun altro modo di
 *    sapere che sotto è cambiato tutto (WCAG 4.1.3).
 */
export type Tone = 'ok' | 'danger' | 'quiet';

export type Announce = {
  /**
   * Scrive il messaggio e lo annuncia. Stabile fra un disegno e l'altro.
   *
   * Il tono è facoltativo perché quasi tutti gli esiti sono neutri: si dice
   * cos'è successo, e basta.
   *
   * Un testo **vuoto** toglie il banner e non lascia niente da rileggere. Serve
   * dove il messaggio ha smesso di essere vero per una ragione che non è un
   * messaggio nuovo: un thread di supervisione che si apre dopo un caricamento
   * fallito lascerebbe l'errore di prima sopra il thread aperto, cioè due righe
   * che si contraddicono nella stessa schermata.
   */
  say: (text: string, tone?: Tone) => void;
  /** La regione live più il banner: si monta una volta, in cima alla sezione. */
  announcer: ReactNode;
};

export function useAnnounce(): Announce {
  const [state, setState] = useState<{ text: string; seq: number; tone: Tone }>({
    text: '',
    seq: 0,
    tone: 'ok',
  });

  const say = useCallback((text: string, tone: Tone = 'ok') => {
    setState((previous) => ({ text, seq: previous.seq + 1, tone }));
  }, []);

  const announcer = useMemo(
    () => (
      <>
        <Announcer polite={state.text} assertive="" seq={state.seq} />
        {state.text && state.tone !== 'quiet' ? (
          <p className={state.tone === 'danger' ? 'banner banner--danger' : 'banner'}>
            {state.text}
          </p>
        ) : null}
      </>
    ),
    [state.text, state.seq, state.tone],
  );

  return { say, announcer };
}
